/**
 * requestTimeout.ts — bound a request in time, without lying about why it ended.
 *
 * WHY THIS EXISTS
 * ===============
 * 2026-08-31, mattmayer (`e9378d10`): a wifi transition left the device's
 * network path to our API half-dead. The app then made ZERO requests for
 * 8 hours 43 minutes. It never crashed, never showed an error, and never
 * recovered. The API was healthy throughout (10 other users active); his
 * own browser loaded `api.coachcedar.com/health` fine mid-outage; a
 * force-quit did NOT fix it; only an airplane-mode toggle did.
 *
 * A hung TCP connection with no timeout produces a promise that never
 * settles. Not an error — nothing. No retry fires, no failure banner
 * renders, no telemetry is emitted, and nothing reaches the wire for us
 * to observe. The failure IS the absence of packets, which is exactly why
 * there is no server-side marker for it and never can be.
 *
 * `offlineCompletionQueue` already learned this lesson in the small
 * (`DRAIN_REQUEST_TIMEOUT_MS`, :46). This module generalises the pattern
 * so the canonical HTTP entrypoint can adopt it without every caller
 * hand-rolling an AbortController — there were 18 hand-rolled copies at
 * the time of writing, each subtly different.
 *
 * WHAT THIS IS NOT
 * ----------------
 * - Not a retry policy. A timeout means "we learned nothing"; deciding
 *   what to do about that belongs to the caller (see `offlineRetryPolicy`).
 * - Not a deadline across attempts. Each attempt gets its own budget.
 *   A 401-refresh-retry legitimately doubles wall time; charging both
 *   attempts to one budget would abort healthy requests.
 * - Not a cancellation API. Callers that want to cancel still pass their
 *   own `signal`; this composes with it rather than replacing it.
 *
 * ─────────────────────────────────────────────────────────────────────
 * FOUR RUNTIME CONSTRAINTS — all verified against React Native 0.79.
 * Every one of them has a tempting modern alternative that does NOT work
 * here. Do not "modernise" this file without re-verifying on a device.
 * ─────────────────────────────────────────────────────────────────────
 *
 * 1. `AbortSignal.timeout()` DOES NOT EXIST. Use `setTimeout` + `abort()`.
 *    It exists in jsdom, so a green jest run is NOT evidence it works.
 *    It is only safe in `.web.tsx` files.
 *
 * 2. `AbortSignal.any()` DOES NOT EXIST. Composition is manual: our own
 *    controller, plus a `{once:true}` listener on the caller's signal.
 *
 * 3. `signal.reason` and `signal.throwIfAborted()` ARE UNDEFINED. This is
 *    why we track `timedOut` on a closure flag instead of reading it back
 *    off the signal. (`useChatLogic.ts:152` is a pre-existing latent bug
 *    of exactly this shape — it reads `.reason` and always gets undefined.)
 *
 * 4. THE ERROR MUST BE NAMED `AbortError`. Several cold-launch and chat
 *    error branches carve out aborts BY NAME, not semantically — e.g.
 *    `app/index.tsx:383-386`. An error named anything else would fall
 *    through into an auth branch and, at `Chat.tsx:6678`, delete the JWT.
 *    A timeout must never be able to end a session.
 */

/**
 * Default per-attempt budget.
 *
 * 30s matches `DRAIN_REQUEST_TIMEOUT_MS` in `offlineCompletionQueue` — the
 * one place in the app that already had to pick this number and defend it.
 * The reasoning carries over: far longer than any healthy request, far
 * shorter than "forever".
 *
 * Deliberately NOT lower. The choke point fronts the health-sync family
 * (`/api/native/apple-health/sync`, `/api/native/health/*`,
 * `/api/sync/missed-*-cron`), which submit user-sized batches — a 10-15s
 * budget risks aborting a large, legitimate backfill on a slow connection
 * and would turn a reliability fix into a data-sync regression.
 *
 * Chat is NOT affected by this constant. Chat streaming never traverses
 * the choke point: native uses `react-native-sse` (`useChatLogic.ts:3810`)
 * and web uses fetch+ReadableStream (`:3683`), both on raw `fetch`. Chat's
 * own 150s budget (`CHAT_REQUEST_TIMEOUT`) is untouched.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Budget for the background health-sync family (`/api/native/*`).
 *
 * These are NOT one budget with the interactive path. Measured over 18.5
 * days of production `SLOW_REQUEST` markers (services/slow_request_logger.py),
 * the choke-point traffic splits into two populations that are nowhere near
 * each other:
 *
 *   interactive (non-/api/native/*) 2xx : p95 4.2s   p99 7.4s   p99.9 32.4s
 *   background  health-sync        2xx : p95 33.7s  p99 62.8s  p99.9 250.4s
 *
 * A single 30s budget would have aborted 488 SUCCESSFUL health syncs in that
 * window — turning a reliability fix into a data-sync regression, which on
 * iOS is worse than it sounds because the HealthKit anchor has already
 * advanced by then and those samples are not re-fetched.
 *
 * 5 minutes is deliberately generous. Nobody is watching a background sync,
 * so the job here is only to make "forever" impossible — not to be
 * responsive. It still cuts the pathological tail: 7 requests exceeded 300s
 * in 18.5 days, and they correlate with clients that vanished mid-upload.
 *
 * ⚠️ The instrument that produced these numbers fires in a `finally`, so it
 * can only see requests that FINISHED. The hung population — the one this
 * whole module exists for — is structurally invisible to it. Read these
 * percentiles as a floor on how long legitimate work takes, never as
 * evidence about how often hangs occur.
 */
export const HEALTH_SYNC_TIMEOUT_MS = 300_000;

/**
 * Routes that belong to the background health-sync population.
 *
 * Matched on the path so the budget cannot depend on 34 separate call sites
 * each remembering to pass an override — several of them reach `fetch`
 * through `apiCall`, which does not forward per-call options. Callers may
 * still override explicitly; this only picks the default.
 */
// `/api/native/…` covers the HealthKit / Health-Connect family; the cron
// catch-ups are `/api/sync/missed-daily-cron` and `-weekly-cron` — a HYPHEN,
// not a path segment, which an earlier version of this pattern got wrong.
const HEALTH_SYNC_PATH_PATTERN = /\/api\/(native\/|sync\/missed-)/;

/** Choose the default budget for a URL. Exported for direct testing. */
export function resolveTimeoutForUrl(url: string): number {
  return HEALTH_SYNC_PATH_PATTERN.test(String(url))
    ? HEALTH_SYNC_TIMEOUT_MS
    : DEFAULT_REQUEST_TIMEOUT_MS;
}

/**
 * An abort we caused because the budget elapsed.
 *
 * `name` is 'AbortError' to satisfy constraint 4 above — every existing
 * name-based carve-out keeps working unchanged. `isTimeout` is the additive
 * bit that lets a caller tell "we ran out of time" from "the user cancelled",
 * which matters because those want opposite handling: a timeout should be
 * retried, a user cancellation should not.
 */
export interface RequestTimeoutError extends Error {
  name: 'AbortError';
  isTimeout: true;
}

/** True for any abort — ours or the caller's. Duck-typed on purpose. */
export function isAbortError(err: unknown): boolean {
  // Never `instanceof DOMException`: it is not reliably present in RN, and
  // whatwg-fetch's abort rejection is constructed differently across
  // platforms. The name is the only portable signal.
  return !!err && typeof err === 'object' && (err as { name?: unknown }).name === 'AbortError';
}

/** True only for an abort this module caused by running out of budget. */
export function isTimeoutError(err: unknown): boolean {
  return isAbortError(err) && (err as { isTimeout?: unknown }).isTimeout === true;
}

/** Build the error we throw when our own budget elapses. */
export function makeTimeoutError(timeoutMs: number, url?: string): RequestTimeoutError {
  // Message carries NO auth vocabulary. `Chat.tsx` and friends match some
  // branches on message content, and a timeout must never read as an auth
  // failure (constraint 4).
  const where = url ? ` (${url})` : '';
  const err = new Error(`Request timed out after ${timeoutMs}ms${where}`) as RequestTimeoutError;
  err.name = 'AbortError';
  err.isTimeout = true;
  return err;
}

export interface TimeoutScope {
  /** Pass this to `fetch`. Aborts on OUR budget or on the caller's signal. */
  signal: AbortSignal;
  /** True once our budget elapsed (as opposed to the caller aborting). */
  timedOut: () => boolean;
  /**
   * Clear the timer and detach the caller listener. MUST be called from a
   * `finally` on every path including throw — an abort timer that outlives
   * its request fires later against a controller nobody is listening to,
   * and on a retry loop that leaks one live timer per attempt.
   */
  dispose: () => void;
}

/**
 * Create a per-attempt timeout scope, composed with an optional caller signal.
 *
 * Returns `null` when there is nothing to bound (`timeoutMs <= 0`), so a
 * caller can opt out — streaming paths must, since a body that streams for
 * minutes is healthy, not hung.
 *
 * @throws the caller's abort immediately if `callerSignal` is ALREADY
 *         aborted. This is deliberate: issuing a fetch with an
 *         already-aborted signal is the one shape that can leave the
 *         whatwg-fetch promise unsettled — i.e. it would reproduce the
 *         very hang we are fixing.
 */
export function createTimeoutScope(
  callerSignal: AbortSignal | null | undefined,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
  url?: string,
): TimeoutScope | null {
  if (!(timeoutMs > 0)) return null;

  if (callerSignal?.aborted) {
    // Do not start a request that is already cancelled. Surface it as a
    // plain abort (NOT a timeout — the caller cancelled, we did not
    // run out of time).
    const err = new Error('Request aborted before it was issued');
    err.name = 'AbortError';
    throw err;
  }

  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    // Flag BEFORE aborting: the abort rejection can be observed
    // synchronously by the awaiting frame, and it must already be able to
    // see that this was a timeout rather than a cancellation.
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  // Manual composition — constraint 2. `{once:true}` so a caller signal
  // reused across attempts cannot accumulate listeners.
  const onCallerAbort = () => controller.abort();
  if (callerSignal) {
    try {
      callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    } catch (e) {
      // A malformed signal (a hand-rolled `{aborted:false}` stub in a test,
      // or a non-DOM environment) must not leave the timer we just armed
      // running with no `finally` to reach it — 30s later it would abort a
      // controller nobody holds and surface as an open handle.
      clearTimeout(timer);
      throw e;
    }
  }

  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    dispose: () => {
      clearTimeout(timer);
      // Explicit removal as well as `{once:true}`: the listener only fires
      // once, but if it never fires it would otherwise stay attached to a
      // caller signal that outlives this attempt.
      if (callerSignal) {
        callerSignal.removeEventListener('abort', onCallerAbort);
      }
    },
  };
}

/**
 * Re-throw an abort with timeout attribution attached.
 *
 * `fetch` rejects with its own AbortError object that we do not control, so
 * "was this our budget or the caller's cancel?" is only answerable from the
 * scope. Call this from the catch so the distinction survives to the caller.
 */
/**
 * Bound a promise that is NOT covered by an abort signal.
 *
 * WHY THIS EXISTS
 * ===============
 * `fetch` resolves on RESPONSE HEADERS, not on the body. Both of this
 * module's abort mechanisms are therefore already disarmed by the time a
 * caller reads the body:
 *   - `fetchWithAuth` disposes its scope in a `finally` that runs when the
 *     fetch resolves (deliberately — an abort timer that outlives its request
 *     leaks), and
 *   - a caller that passed its own controller has typically cleared its timer
 *     on the line after the await.
 * So `await res.json()` is unbounded, and on a half-open socket — the shape a
 * phone produces when connectivity flaps mid-request — it NEVER SETTLES. Not
 * slow: never. A `.catch()` cannot save it, because a promise that does not
 * settle never rejects.
 *
 * That is not a hypothetical: it wedged a workout completion on the
 * "Saving workout…" screen overnight (2026-09-10), with the completion neither
 * saved nor queued — the offline queue lives in the caller's `catch`, which is
 * unreachable when the promise simply hangs.
 *
 * Losing a response BODY is nearly always survivable (the request already
 * succeeded server-side); hanging forever never is. So this rejects, and the
 * caller decides — usually by falling back to a default.
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  url?: string,
): Promise<T> {
  if (!(timeoutMs > 0)) return promise;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(makeTimeoutError(timeoutMs, url)), timeoutMs);
      }),
    ]);
  } finally {
    // Always clear: a surviving timer holds a reference and, in tests, keeps
    // the event loop alive past the assertion.
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** How long a response BODY may take once its headers have landed. */
export const BODY_READ_TIMEOUT_MS = 20_000;

export function attributeAbort(err: unknown, scope: TimeoutScope | null, timeoutMs: number, url?: string): unknown {
  if (scope && isAbortError(err) && scope.timedOut()) {
    return makeTimeoutError(timeoutMs, url);
  }
  return err;
}
